import type {
  LogicalBounds,
  LogicalSize,
  ResponsiveLayoutConfig,
  ResponsiveLayoutSnapshot,
  SafeInsets,
} from './LayoutTypes.js';

const sanitizeDimension = (value: number, fallback: number) => (
  Number.isFinite(value) && value > 0 ? value : fallback
);

function createBounds(x: number, y: number, width: number, height: number): LogicalBounds {
  const safeWidth = Math.max(0, width);
  const safeHeight = Math.max(0, height);
  return {
    x,
    y,
    width: safeWidth,
    height: safeHeight,
    right: x + safeWidth,
    bottom: y + safeHeight,
    centerX: x + safeWidth * 0.5,
    centerY: y + safeHeight * 0.5,
  };
}

function normalizeInsets(insets: SafeInsets | undefined): Required<SafeInsets> {
  return {
    top: Math.max(0, insets?.top ?? 0),
    right: Math.max(0, insets?.right ?? 0),
    bottom: Math.max(0, insets?.bottom ?? 0),
    left: Math.max(0, insets?.left ?? 0),
  };
}

/**
 * Pure responsive-layout calculation. It never changes Phaser scale, camera,
 * scene, or gameplay state, so callers can recompute it freely during resize.
 */
export function calculateResponsiveLayout(
  viewport: LogicalSize,
  config: ResponsiveLayoutConfig,
): ResponsiveLayoutSnapshot {
  const logicalWidth = sanitizeDimension(config.logicalSize.width, 1280);
  const logicalHeight = sanitizeDimension(config.logicalSize.height, 720);
  const viewportWidth = sanitizeDimension(viewport.width, logicalWidth);
  const viewportHeight = sanitizeDimension(viewport.height, logicalHeight);
  const normalizedViewport = { width: viewportWidth, height: viewportHeight };
  const logicalSize = { width: logicalWidth, height: logicalHeight };
  let visible = createBounds(0, 0, logicalWidth, logicalHeight);

  if ((config.visibilityScaleMode ?? 'envelop') === 'envelop') {
    const viewportAspect = viewportWidth / viewportHeight;
    const logicalAspect = logicalWidth / logicalHeight;

    if (viewportAspect > logicalAspect) {
      const visibleHeight = logicalWidth / viewportAspect;
      visible = createBounds(0, (logicalHeight - visibleHeight) * 0.5, logicalWidth, visibleHeight);
    } else if (viewportAspect < logicalAspect) {
      const visibleWidth = logicalHeight * viewportAspect;
      visible = createBounds((logicalWidth - visibleWidth) * 0.5, 0, visibleWidth, logicalHeight);
    }
  }

  const insets = normalizeInsets(config.safeInsets);
  const safe = createBounds(
    visible.x + insets.left,
    visible.y + insets.top,
    visible.width - insets.left - insets.right,
    visible.height - insets.top - insets.bottom,
  );
  const compact = (
    (config.compactViewportHeight !== undefined
      && viewportHeight < config.compactViewportHeight)
    || (config.compactVisibleHeight !== undefined
      && safe.height < config.compactVisibleHeight)
    || (config.compactVisibleWidth !== undefined
      && safe.width < config.compactVisibleWidth)
  );

  return {
    visible,
    safe,
    viewport: normalizedViewport,
    logicalSize,
    insets,
    mode: compact ? 'compact' : 'standard',
    compact,
  };
}

export function getSceneParentSize(scene: Phaser.Scene): LogicalSize {
  return {
    width: scene.scale.parentSize.width || window.innerWidth,
    height: scene.scale.parentSize.height || window.innerHeight,
  };
}

/** Read-only Phaser adapter around the pure calculation. */
export function getSceneResponsiveLayout(
  scene: Phaser.Scene,
  config: ResponsiveLayoutConfig,
): ResponsiveLayoutSnapshot {
  return calculateResponsiveLayout(getSceneParentSize(scene), config);
}
